/**
 * Vista de operador — the Marposs bench in Spanish, for the person standing at it.
 *
 * WHY THIS EXISTS, and why it is not a translation of the engineering view:
 *
 * The SPC screen answers "what is the process doing?" — nineteen characteristics, trends, swimlanes,
 * channel states. That is the right screen for an engineer holding a question. It is the wrong screen
 * for an operator holding a part, because it presents a CORRECTION as a row of coefficients
 * (`avg 2.9 · err -12.5 · 24% band · -0.013 mm`) and expects the reader to reconstruct the causal
 * chain themselves. They will not, and on a screen that writes to a live control that matters:
 * approving something you do not understand is the failure mode this whole loop exists to prevent.
 *
 * So this view answers three questions in order, in words, and nothing else:
 *
 *   1. QUE MIDIO EL BANCO   — where the part is, against where it should be
 *   2. QUE HAY QUE CORREGIR — which machine, which tool, which wear axis, how much
 *   3. QUE VA A PASAR       — where the measurement should land afterwards
 *
 * Spanish because the gauge already is: every characteristic name on this bench
 * (`Diametro de Piloto`, `Espesor de Frenaje`) and every tool description (`... acabado`) comes
 * from the customer's own documents in Spanish. The English was only ever our chrome around their
 * data, and it was the chrome the operator had to read.
 *
 * NOTHING IS DERIVED HERE. Every number rendered below — target, predicted landing point, predicted
 * movement — is computed by the bench service and carried on the proposal. A second implementation
 * of the physics in the client is how two surfaces end up disagreeing about which way a correction
 * moves the part, and a quality screen that contradicts itself is worse than one that stays quiet.
 */
import { useMemo } from 'react';

const fmtUm = v => (v === null || v === undefined ? '—' : `${Number(v) > 0 ? '+' : ''}${Number(v).toFixed(1)}`);
const fmtMm = v => (v === null || v === undefined ? '—' : `${Number(v) > 0 ? '+' : ''}${Number(v).toFixed(3)}`);

/** ARRIBA / ABAJO — stated as a direction, never as a raw sign the reader has to interpret. */
const sideWord = d => (d > 0 ? 'arriba' : 'abajo');

/** The Spanish role at the end of the customer's own tooling string: "... · acabado" → "acabado". */
function toolRole(tooling) {
  if (!tooling) return null;
  const parts = String(tooling).split('·').map(s => s.trim()).filter(Boolean);
  return parts.length > 1 ? parts[parts.length - 1] : null;
}

function severityOf(c, actionLimit) {
  if (c.value === null || c.value === undefined) return 'empty';
  if (c.verdict === 'Fuera') return 'bad';
  if (Math.abs(Number(c.bandFraction) || 0) >= actionLimit) return 'warn';
  return 'good';
}

const TONE = {
  good:  { dot: 'bg-emerald-400', text: 'text-emerald-300', ring: 'border-emerald-500/30', word: 'BIEN' },
  warn:  { dot: 'bg-amber-400',   text: 'text-amber-300',   ring: 'border-amber-500/40',   word: 'VIGILAR' },
  bad:   { dot: 'bg-red-400',     text: 'text-red-300',     ring: 'border-red-500/45',     word: 'FUERA' },
  empty: { dot: 'bg-slate-500',   text: 'text-slate-400',   ring: 'border-slate-700',      word: 'SIN DATO' },
};

/**
 * The tolerance bar, with the correction drawn ON it.
 *
 * A number like "-0.013 mm" means nothing without direction. Showing the current position and the
 * predicted position on the same band, joined by an arrow, is the one piece of this screen that is
 * understood before it is read.
 */
function Banda({ c, actionLimit, predicted = null, big = false }) {
  const span = Math.max(1e-9, Number(c.hi) - Number(c.lo));
  const pct = v => Math.max(0, Math.min(100, ((Number(v) - Number(c.lo)) / span) * 100));
  const sev = severityOf(c, actionLimit);
  const pos = c.value === null || c.value === undefined ? null : pct(c.value);
  const predPos = predicted === null ? null : pct(predicted);

  return (
    <div>
      <div className={`relative w-full rounded-full bg-slate-800 ${big ? 'h-5' : 'h-3'}`}>
        <div
          className="absolute inset-y-0 bg-emerald-500/15"
          style={{
            left:  `${pct(Number(c.target) - actionLimit * (Number(c.target) - Number(c.lo)))}%`,
            width: `${pct(Number(c.target) + actionLimit * (Number(c.hi) - Number(c.target)))
                     - pct(Number(c.target) - actionLimit * (Number(c.target) - Number(c.lo)))}%`,
          }}
        />
        <div className="absolute inset-y-[-3px] w-0.5 bg-slate-300/70" style={{ left: `${pct(c.target)}%` }} />

        {/* Where the correction is predicted to put it — drawn first, so the live dot sits on top. */}
        {predPos !== null && (
          <>
            <div
              className="absolute inset-y-0 bg-cyan-400/25"
              style={{ left: `${Math.min(pos, predPos)}%`, width: `${Math.abs(pos - predPos)}%` }}
            />
            <div
              className={`absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-cyan-300 bg-slate-950 ${big ? 'h-5 w-5' : 'h-3.5 w-3.5'}`}
              style={{ left: `${predPos}%` }}
              title="donde deberia quedar"
            />
          </>
        )}

        {pos !== null && (
          <div
            className={`absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-slate-950 ${big ? 'h-6 w-6' : 'h-4 w-4'} ${
              sev === 'bad' ? 'bg-red-400' : sev === 'warn' ? 'bg-amber-400' : 'bg-emerald-400'
            }`}
            style={{ left: `${pos}%` }}
          />
        )}
      </div>
      <div className={`mt-1 flex justify-between font-mono text-slate-500 ${big ? 'text-xs' : 'text-[10px]'}`}>
        <span>{c.lo}</span>
        <span className="text-slate-400">objetivo {c.target}</span>
        <span>{c.hi}</span>
      </div>
    </div>
  );
}

/** One of the three dimensions the line can actually correct. */
function Medida({ c, actionLimit, esperando }) {
  const sev = severityOf(c, actionLimit);
  const tone = TONE[sev];
  return (
    <div className={`rounded-2xl border bg-slate-900/50 p-4 ${esperando ? 'border-amber-400/70 ring-1 ring-amber-400/25' : tone.ring}`}>
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-xs font-black text-slate-500">{c.id}</span>
        <span className="truncate text-base font-semibold text-slate-100">{c.name}</span>
        <span className={`ml-auto flex items-center gap-1.5 text-[11px] font-black ${tone.text}`}>
          <span className={`h-2 w-2 rounded-full ${tone.dot}`} />
          {esperando ? 'ESPERA VISTO BUENO' : tone.word}
        </span>
      </div>
      <div className="mt-2 flex items-end gap-2">
        <span className="font-mono text-4xl font-black tabular-nums text-slate-50">{fmtUm(c.value)}</span>
        <span className="pb-1 font-mono text-sm text-slate-500">µm</span>
      </div>
      <div className="mt-3"><Banda c={c} actionLimit={actionLimit} /></div>
      {c.correctedBy && (
        <div className="mt-3 text-xs text-slate-500">
          Se corrige en <span className="font-bold text-amber-200">{c.correctedBy.station}</span>
          {' · herramienta '}<span className="font-bold text-amber-200">{c.correctedBy.toolKey}</span>
          {' · desgaste '}<span className="font-bold text-amber-200">{c.correctedBy.family}</span>
        </div>
      )}
    </div>
  );
}

function Paso({ n, titulo, children }) {
  return (
    <div className="flex gap-3">
      <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-700 font-mono text-sm font-black text-slate-200">
        {n}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-black uppercase tracking-[0.14em] text-slate-500">{titulo}</div>
        <div className="mt-1.5">{children}</div>
      </div>
    </div>
  );
}

/**
 * The correction, as three answered questions.
 *
 * `c` is the characteristic the proposal is about; it supplies the band so the same picture the
 * operator has been watching is the one the correction is drawn on.
 */
function Correccion({ p, c, actionLimit, maxStepMm, busy, onAprobar, onRechazar }) {
  const role = toolRole(p.tooling);
  const desviacion = p.smoothedUm - p.targetUm;
  const pctBanda = Math.round(Math.abs(p.bandFraction) * 100);
  // A correction deliberately does NOT chase the whole error: the loop moves a fraction of it and
  // never more than one step. On a badly drifted characteristic that looks like the screen is
  // under-correcting, so say plainly that it is a limit rather than leaving it to be noticed.
  const topado = maxStepMm != null && Math.abs(p.deltaMm) >= maxStepMm - 1e-9;

  return (
    <section className="rounded-2xl border-2 border-amber-400/60 bg-amber-500/[0.07] p-5">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-lg font-black tracking-tight text-amber-100">Corrección sugerida</h3>
        <span className="rounded-full bg-amber-400/15 px-2.5 py-0.5 font-mono text-[11px] font-bold text-amber-200">{p.id}</span>
        <span className="ml-auto font-mono text-[11px] text-slate-500">medición #{p.measurementId} · pieza {p.partCountAtProposal}</span>
      </div>

      <div className="mt-4 space-y-5">
        <Paso n="1" titulo="Qué midió el banco">
          <div className="text-base font-semibold text-slate-100">{p.characteristic}</div>
          {c && <div className="mt-2"><Banda c={c} actionLimit={actionLimit} predicted={p.predictedUm} big /></div>}
          <div className="mt-3 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
            <div className="flex justify-between border-b border-slate-800/70 py-1">
              <span className="text-slate-400">Promedio de {p.samplesUsed} piezas</span>
              <span className="font-mono font-bold text-slate-100">{fmtUm(p.smoothedUm)} µm</span>
            </div>
            <div className="flex justify-between border-b border-slate-800/70 py-1">
              <span className="text-slate-400">Objetivo</span>
              <span className="font-mono font-bold text-slate-100">{fmtUm(p.targetUm)} µm</span>
            </div>
          </div>
          <p className="mt-2 text-sm text-slate-300">
            Se fue <b className="text-amber-200">{Math.abs(desviacion).toFixed(1)} µm {sideWord(desviacion)}</b> del objetivo,
            o sea al <b className="text-amber-200">{pctBanda} %</b> de la tolerancia permitida.
          </p>
        </Paso>

        <Paso n="2" titulo="Qué hay que corregir">
          <div className="grid gap-2 sm:grid-cols-4">
            <div className="rounded-xl bg-slate-950/50 px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Máquina</div>
              <div className="font-mono text-xl font-black text-slate-50">{p.station}</div>
            </div>
            <div className="rounded-xl bg-slate-950/50 px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Herramienta</div>
              <div className="font-mono text-xl font-black text-slate-50">{p.toolKey}</div>
              {role && <div className="text-[10px] text-slate-500">{role}</div>}
            </div>
            <div className="rounded-xl bg-slate-950/50 px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Desgaste</div>
              <div className="font-mono text-xl font-black text-slate-50">{p.family}</div>
            </div>
            <div className="rounded-xl border border-amber-400/40 bg-amber-400/10 px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-amber-200/70">Mover</div>
              <div className="font-mono text-xl font-black text-amber-100">{fmtMm(p.deltaMm)}</div>
              <div className="text-[10px] text-amber-200/60">mm</div>
            </div>
          </div>
          {p.tooling && <div className="mt-2 truncate text-[11px] text-slate-500" title={p.tooling}>{p.tooling}</div>}
        </Paso>

        <Paso n="3" titulo="Qué va a pasar">
          <p className="text-sm text-slate-300">
            La medida debería moverse <b className="text-cyan-200">{Math.abs(p.expectedEffectUm).toFixed(1)} µm {sideWord(p.expectedEffectUm)}</b>
            {' '}y quedar cerca de <b className="text-cyan-200">{fmtUm(p.predictedUm)} µm</b>, con el objetivo en {fmtUm(p.targetUm)} µm.
          </p>
          {topado && (
            <p className="mt-1.5 rounded-lg border border-slate-700/60 bg-slate-950/40 px-3 py-2 text-xs text-slate-300">
              No corrige todo de una vez: el máximo por ajuste es <b className="font-mono text-slate-100">{maxStepMm.toFixed(3)} mm</b>.
              Si la medida sigue desviada, el banco va a sugerir otra corrección más adelante.
            </p>
          )}
          <p className="mt-1.5 text-xs text-slate-500">
            El cambio se aplica en {p.station}, así que las piezas que ya están entre {p.station} y el
            banco salen con la medida vieja. El efecto se ve unas piezas después.
          </p>
        </Paso>
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <button
          disabled={busy}
          onClick={() => onAprobar(p)}
          className="flex-1 rounded-xl bg-emerald-600 px-5 py-4 text-base font-black tracking-wide text-white hover:bg-emerald-500 disabled:opacity-40"
        >
          APLICAR CORRECCIÓN
        </button>
        <button
          disabled={busy}
          onClick={() => onRechazar(p)}
          className="rounded-xl border border-slate-600 bg-slate-900 px-6 py-4 text-base font-bold text-slate-300 hover:bg-slate-800 disabled:opacity-40"
        >
          No aplicar
        </button>
      </div>
    </section>
  );
}

const ESTADO = {
  'NO DATA':           { es: 'SIN DATOS',          tone: 'text-slate-300',   ring: 'border-slate-700',      dot: 'bg-slate-500' },
  'PROCESS ALERT':     { es: 'ALERTA DE PROCESO',  tone: 'text-red-200',     ring: 'border-red-500/50',     dot: 'bg-red-400' },
  'AWAITING APPROVAL': { es: 'ESPERA VISTO BUENO', tone: 'text-amber-200',   ring: 'border-amber-500/50',   dot: 'bg-amber-400' },
  'DRIFT WATCH':       { es: 'VIGILAR DESVIACIÓN', tone: 'text-amber-200',   ring: 'border-amber-500/35',   dot: 'bg-amber-400' },
  'IN CONTROL':        { es: 'EN CONTROL',         tone: 'text-emerald-200', ring: 'border-emerald-500/35', dot: 'bg-emerald-400' },
};

function BenchSimple({ view, actionLimit, estado, busy, notice, onAprobar, onRechazar, onMedir }) {
  const chars = view?.characteristics || [];
  const correctable = chars.filter(c => c.correctable);
  const proposals = view?.proposals || [];
  const alerts = view?.alerts || [];
  const byId = useMemo(() => new Map(chars.map(c => [c.id, c])), [chars]);
  const proposalIds = useMemo(() => new Set(proposals.map(p => p.characteristicId)), [proposals]);

  const fuera = chars.filter(c => severityOf(c, actionLimit) === 'bad').length;
  const vigilar = chars.filter(c => severityOf(c, actionLimit) === 'warn').length;
  const est = ESTADO[estado] || ESTADO['NO DATA'];

  return (
    <div className="mx-auto max-w-[1100px] space-y-5 p-5">
      <div className={`flex flex-wrap items-center gap-4 rounded-2xl border-2 bg-slate-900/40 px-5 py-4 ${est.ring}`}>
        <span className={`h-4 w-4 rounded-full ${est.dot}`} />
        <span className={`text-2xl font-black tracking-tight ${est.tone}`}>{est.es}</span>
        <div className="ml-auto flex gap-6 text-center">
          <div>
            <div className="font-mono text-2xl font-black text-slate-100">{view?.partCount ?? 0}</div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500">piezas</div>
          </div>
          <div>
            <div className={`font-mono text-2xl font-black ${fuera ? 'text-red-300' : 'text-slate-100'}`}>{fuera}</div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500">fuera</div>
          </div>
          <div>
            <div className={`font-mono text-2xl font-black ${vigilar ? 'text-amber-300' : 'text-slate-100'}`}>{vigilar}</div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500">a vigilar</div>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onMedir(1)}
            disabled={busy}
            className="rounded-xl bg-slate-700 px-5 py-3 text-sm font-black text-slate-100 hover:bg-slate-600 disabled:opacity-40"
          >
            MEDIR PIEZA
          </button>
          <button
            onClick={() => onMedir(10)}
            disabled={busy}
            className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-sm font-bold text-slate-400 hover:bg-slate-800 disabled:opacity-40"
            title="medir diez piezas seguidas"
          >
            ×10
          </button>
        </div>
      </div>

      {notice && <div className="rounded-xl bg-slate-900/60 px-4 py-2 font-mono text-xs text-slate-400">{notice}</div>}

      {proposals.length > 0
        ? proposals.map(p => (
            <Correccion
              key={p.id}
              p={p}
              c={byId.get(p.characteristicId)}
              actionLimit={actionLimit}
              maxStepMm={view?.tuning?.maxStepMm ?? null}
              busy={busy}
              onAprobar={onAprobar}
              onRechazar={onRechazar}
            />
          ))
        : (
          <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.05] px-5 py-6 text-center">
            <div className="text-lg font-bold text-emerald-200">No hay ninguna corrección pendiente</div>
            <div className="mt-1 text-sm text-slate-400">
              El banco avisa solo cuando una medida corregible pasa el {Math.round(actionLimit * 100)} % de su tolerancia.
            </div>
          </div>
        )}

      {alerts.length > 0 && (
        <section className="rounded-2xl border border-red-500/35 bg-red-500/[0.06] p-4">
          <h3 className="text-sm font-black uppercase tracking-wide text-red-200">Avisos de proceso — no se arreglan con desgaste</h3>
          <div className="mt-2 space-y-1.5">
            {alerts.map(a => (
              <div key={a.characteristicId} className="flex flex-wrap items-baseline gap-2 rounded-lg bg-slate-950/40 px-3 py-2 text-sm">
                <span className="font-mono text-xs text-slate-500">{a.characteristicId}</span>
                <span className="font-semibold text-red-100">{a.name}</span>
                <span className="ml-auto font-mono text-slate-300">{fmtUm(a.value)} µm</span>
                <span className="w-full text-xs text-slate-400">Llamar a calidad: no hay desgaste que mueva esta medida.</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <h3 className="mb-2 text-[11px] font-black uppercase tracking-[0.14em] text-slate-500">
          Las tres medidas que sí se corrigen con desgaste
        </h3>
        <div className="grid gap-3 md:grid-cols-3">
          {correctable.map(c => (
            <Medida key={c.id} c={c} actionLimit={actionLimit} esperando={proposalIds.has(c.id)} />
          ))}
        </div>
        <p className="mt-2 text-xs text-slate-600">
          Las otras {Math.max(0, chars.length - correctable.length)} características del banco son de forma, alabeo y
          variación: se miden y se vigilan, pero ninguna se arregla moviendo un desgaste.
        </p>
      </section>
    </div>
  );
}

export default BenchSimple;
